/**
 * ACCOUNT MOCK DATA
 *
 * API name Account__c. List views match Client list view pattern for consistent UI.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { Account } from '../../types/index';
import type { ListView } from '../../types/listView';

/**
 * DEFAULT ACCOUNT RECORDS
 * type: 'firm' | 'individual'. Firms used for pricebooks; individuals are person accounts.
 */
export const defaultAccountRecords: Account[] = [
  { id: 'acc_001', name: 'Acme Law Firm', type: 'firm', description: 'Full-service law firm', accountOwner: 'Admin' },
  { id: 'acc_002', name: 'Global Partners LLC', type: 'firm', description: 'Global partner organization', accountOwner: 'Admin' },
  { id: 'acc_003', name: 'Summit Financial Group', type: 'firm', description: 'Financial services firm', accountOwner: 'Admin' },
  { id: 'acc_004', name: 'Riverside Legal Associates', type: 'firm', description: 'Uses default pricebook', accountOwner: 'Admin' },
  { id: 'acc_005', name: 'Metro Credit Advisors', type: 'firm', description: 'Credit advisory firm', accountOwner: 'Admin' },
  { id: 'acc_006', name: 'Pacific Coast Financial', type: 'firm', description: 'Pacific Coast partners', accountOwner: 'Admin' },
  { id: 'acc_ind_001', name: 'Gladys Hill', type: 'individual', phone: '(555) 100-2000', email: 'updates@regal.com', accountOwner: 'Joana Crisostomo', billingAddress: '456 Tehama Street, San Francisco, CA 94103' },
  { id: 'acc_ind_002', name: 'James Wilson', type: 'individual', phone: '(555) 234-5678', email: 'j.wilson@example.com', accountOwner: 'Sarah Johnson' },
  { id: 'acc_ind_003', name: 'Maria Santos', type: 'individual', phone: '(555) 300-4000', email: 'm.santos@example.com', accountOwner: 'Admin', billingAddress: '100 Main St, Austin, TX 78701' }
];

/**
 * DEFAULT ACCOUNT LIST VIEWS (same pattern as Client list views)
 */
export const defaultAccountListViews: ListView[] = [
  {
    id: 'lv_accounts_all',
    name: 'All Accounts',
    apiName: 'all_accounts',
    objectApiName: 'Account',
    description: 'All account records',
    isDefault: true,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Account Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'description', fieldLabel: 'Description', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 5 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_accounts_firms',
    name: 'Firms',
    apiName: 'firms',
    objectApiName: 'Account',
    description: 'Firm accounts only',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Account Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'description', fieldLabel: 'Description', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', isVisible: true, order: 2 },
    ],
    filters: [
      { fieldApiName: 'type', operator: 'equals', value: 'firm' }
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_accounts_individuals',
    name: 'Individuals',
    apiName: 'individuals',
    objectApiName: 'Account',
    description: 'Individual (person) accounts only',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Account Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 1 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 2 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', isVisible: true, order: 3 },
    ],
    filters: [
      { fieldApiName: 'type', operator: 'equals', value: 'individual' }
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  }
];

/**
 * PERSISTED STORAGE
 */
export const persistedAccountRecords = createPersistedMockData(
  'crm_account_records',
  defaultAccountRecords
);

export const persistedAccountListViews = createPersistedMockData(
  'crm_account_list_views',
  defaultAccountListViews
);
